import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, ChevronRight, Compass, Link2, Loader2, RefreshCw, Smartphone, Sunrise, Sunset, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { invitationsApi, onboardingApi } from '../../api/services';
import { setupCopy } from '../../copy/setup';
import type { SetupStatus, SetupStepId } from '../../types/models';
import { SETUP_STEPS, STEP_MINUTES, useSetup } from './SetupProvider';
import { clock, CopyButton, fill, Pill } from './setupParts';
import { joinList } from './routinePrompts';
import { trackSetup } from './telemetry';

// The part-two card at the top of the landing pages (GUIDED_SETUP_BUILD_SPEC
// §2): the Claude checklist, the assistant card for other assistants, or
// nothing (no AI assistant, not asked yet, or hidden).

const OTHER_ASSISTANTS = ['chatgpt', 'copilot', 'gemini'] as const;
type OtherAssistant = typeof OTHER_ASSISTANTS[number];

/** Place at the top of a landing page. Renders the right card, or nothing. */
export function SetupCards() {
  const setup = useSetup();
  const status = setup?.status;
  if (!status || status.cardHidden) return null;
  if (status.aiClient === 'claude') return <SetupChecklistCard status={status} />;
  if (status.aiClient === 'notYet' || OTHER_ASSISTANTS.includes(status.aiClient as OtherAssistant)) {
    return <AssistantCard status={status} />;
  }
  return null;
}

function CardShell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <section className={cn('mb-6 overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm', className)}>
      {children}
    </section>
  );
}

// ── Claude: "Switch Claude on" ──────────────────────────────────────────────

/** An EA's evening routine prepares their executives' next day instead of closing their own. */
export function isEaSetup(status: SetupStatus): boolean {
  return 'prepare' in status.routines;
}

function stepSubtitle(step: SetupStepId, status: SetupStatus): string {
  const c = setupCopy.card.steps;
  if (step === 'routines' && isEaSetup(status)) return c.routines.subEa;
  if (step === 'skills' && isEaSetup(status)) return c.skills.subEa;
  if (step !== 'tools') return c[step].sub;
  const names = Object.keys(status.sources).map((id) => setupCopy.sources[id as keyof typeof setupCopy.sources]?.name ?? id);
  return names.length ? fill(c.tools.sub, { tools: joinList(names) }) : c.tools.subNone;
}

export function SetupChecklistCard({ status }: { status: SetupStatus }) {
  const setup = useSetup();
  const navigate = useNavigate();
  if (!setup) return null;
  const c = setupCopy.card;
  const { doneCount, nextStep, openStep, hideCard } = setup;
  const minutesLeft = SETUP_STEPS.filter((s) => !status.steps[s]?.done).reduce((n, s) => n + STEP_MINUTES[s], 0);
  const routineIds = (['sweep', 'close', 'prepare'] as const).filter((r) => r in status.routines);

  return (
    <CardShell>
      <div className="flex flex-wrap items-start justify-between gap-4 px-6 pb-5 pt-6">
        <div className="min-w-0 max-w-[620px]">
          <p className="text-[12px] font-medium uppercase tracking-[0.07em] text-muted-foreground">{c.eyebrow}</p>
          <h2 className="mt-1 font-serif text-[24px] font-light leading-tight">{status.complete ? c.doneTitle : c.title}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{status.complete ? (isEaSetup(status) ? c.doneSubEa : c.doneSub) : c.sub}</p>
        </div>
        {!status.complete && (
          <div className="w-[230px] shrink-0 text-right">
            <p className="text-[13px] text-muted-foreground">{fill(c.progress, { n: doneCount, minutes: minutesLeft })}</p>
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted" role="progressbar" aria-label={c.progressLabel} aria-valuemin={0} aria-valuemax={4} aria-valuenow={doneCount}>
              <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${(doneCount / 4) * 100}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* The routines, lit once each has run. */}
      <div className="grid border-y border-border sm:grid-cols-2">
        {routineIds.map((r, i) => {
          const routine = status.routines[r];
          const Icon = r === 'sweep' ? RefreshCw : r === 'close' ? Sunset : Sunrise;
          const name = r === 'sweep' ? c.sweepName : r === 'close' ? c.closeName : c.prepareName;
          const gives = r === 'sweep' ? c.sweepGives : r === 'close' ? c.closeGives : c.prepareGives;
          const when = r === 'sweep' && routine.times.length > 1
            ? fill(c.sweepTimes, { first: routine.times[0], last: routine.times[routine.times.length - 1] })
            : routine.times[0] ?? '';
          const lit = !!routine.lastRunAt;
          return (
            <div key={r} className={cn('flex items-start gap-3 px-6 py-4', i > 0 && 'border-t border-border sm:border-l sm:border-t-0')}>
              <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', lit ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground')}>
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="font-plex text-[12px] text-muted-foreground">
                  {when} · {lit ? fill(c.ran, { time: clock(routine.lastRunAt, status.timeZone) }) : c.notYet}
                </p>
                <p className="text-sm font-semibold">{name}</p>
                <p className="text-[12.5px] text-muted-foreground">{gives}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* The four steps; any order, the next one marked. */}
      {!status.complete && (
        <ol className="flex flex-col p-2">
          {SETUP_STEPS.map((step, i) => {
            const done = !!status.steps[step]?.done;
            const isNext = step === nextStep;
            return (
              <li key={step}>
                <button type="button" onClick={() => openStep(step)}
                  className={cn('flex w-full items-center gap-4 rounded-lg px-4 py-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isNext && 'bg-primary/5')}>
                  <span aria-hidden className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-full border text-[13px]',
                    done ? 'border-emerald-500 bg-emerald-500 text-white' : isNext ? 'border-primary text-primary' : 'border-border text-muted-foreground')}>
                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-sm font-semibold', done && 'text-muted-foreground')}>
                      {setupCopy.card.steps[step].title}
                      <span className="sr-only">{` (${done ? c.stepDone : c.stepNotDone})`}</span>
                    </span>
                    <span className="block text-[13px] text-muted-foreground">{stepSubtitle(step, status)}</span>
                  </span>
                  {isNext && <Pill tone="next">{c.upNext}</Pill>}
                  {!done && <span className="shrink-0 text-[12.5px] text-muted-foreground">{fill(c.minutes, { n: STEP_MINUTES[step] })}</span>}
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {/* Optional, not counted. */}
      <div className={cn('px-6 py-4', !status.complete && 'border-t border-border')}>
        {!status.complete && <p className="mb-2 text-[12px] font-medium uppercase tracking-[0.07em] text-muted-foreground">{c.readyTitle}</p>}
        <div className="flex flex-col gap-3">
          {!status.complete && status.features.strategicDrivers && !isEaSetup(status) && (
            <ReadyRow icon={<Compass className="h-4 w-4" aria-hidden />} title={c.driversTitle} sub={c.driversSub}
              action={<CopyButton text={c.driversPrompt} label={c.driversAction} />} />
          )}
          {!status.complete && status.canInviteEa && !isEaSetup(status) && (
            <ReadyRow icon={<Users className="h-4 w-4" aria-hidden />} title={setupCopy.invite.rowTitle} sub={setupCopy.invite.rowSub}
              action={<InviteEaButton />} />
          )}
          {!status.complete && (
            <ReadyRow icon={<Smartphone className="h-4 w-4" aria-hidden />} title={c.devicesTitle} sub={c.devicesSub}
              action={<Button size="sm" variant="outline" className="h-8" onClick={() => navigate('/preferences')}>{c.devicesAction}</Button>} />
          )}
          <div className="flex flex-wrap justify-end gap-2">
            {status.complete && (
              <Button size="sm" variant="outline" className="h-8" onClick={() => navigate('/setup/health')}>{c.seeHealth}</Button>
            )}
            <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={hideCard}>{c.hide}</Button>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

/** Makes a fresh single-use link for the signed-in executive and copies it. */
export function InviteEaButton() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState<string | null>(null);
  const c = setupCopy.invite;

  const make = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { url } = await invitationsApi.create();
      trackSetup('setup_invite_created', { where: 'card' });
      try {
        if (!navigator.clipboard) throw new Error('no clipboard');
        await navigator.clipboard.writeText(url);
        setShown(null);
        toast({ variant: 'success', message: c.copied });
      } catch {
        setShown(url);   // select it by hand
      }
    } catch (e: any) {
      toast({ variant: 'error', message: e?.response?.data?.error || c.failed });
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col items-end gap-2">
      <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={make} disabled={busy}>
        {busy ? <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden /> : <Link2 className="h-3.5 w-3.5" aria-hidden />}
        {busy ? c.making : c.copyLink}
      </Button>
      {shown && (
        <span className="block w-[min(360px,80vw)]">
          <span className="block text-[12px] text-muted-foreground">{c.shown}</span>
          <input readOnly value={shown} aria-label={c.copyLink} onFocus={(e) => e.currentTarget.select()}
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 font-plex text-[12px]" />
        </span>
      )}
    </span>
  );
}

function ReadyRow({ icon, title, sub, action }: { icon: React.ReactNode; title: string; sub: string; action: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-[12.5px] text-muted-foreground">{sub}</p>
      </div>
      {action}
    </div>
  );
}

// ── ChatGPT, Copilot, Gemini, or not yet ────────────────────────────────────

export function AssistantCard({ status }: { status: SetupStatus }) {
  const setup = useSetup();
  const { toast } = useToast();
  const [listed, setListed] = useState(false);
  const [joining, setJoining] = useState(false);
  if (!setup) return null;
  const c = setupCopy.assistantCard;
  const isNotYet = status.aiClient === 'notYet';
  const assistant = isNotYet ? '' : setupCopy.assistants[status.aiClient as OtherAssistant].name;

  const joinWaitlist = async () => {
    if (joining) return;
    setJoining(true);
    try {
      await onboardingApi.joinWaitlist(status.aiClient as OtherAssistant);
      setListed(true);
      trackSetup('setup_client_waitlist', { client: status.aiClient ?? '' });
    } catch {
      toast({ variant: 'error', message: setupCopy.card.saveFailed });   // the button stays; they can try again
    } finally {
      setJoining(false);
    }
  };

  return (
    <CardShell>
      <div className="px-6 py-6">
        <p className="text-[12px] font-medium uppercase tracking-[0.07em] text-muted-foreground">{c.eyebrow}</p>
        <h2 className="mt-1 font-serif text-[24px] font-light leading-tight">
          {isNotYet ? c.notYetTitle : fill(c.otherTitle, { assistant })}
        </h2>
        <p className="mt-2 max-w-[620px] text-sm text-muted-foreground">
          {isNotYet ? (isEaSetup(status) ? c.notYetSubEa : c.notYetSub) : fill(isEaSetup(status) ? c.otherSubEa : c.otherSub, { assistant })}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {isNotYet ? (<>
            <Button asChild size="sm" className="h-8"><a href="https://claude.ai" target="_blank" rel="noopener noreferrer">{c.getClaude}</a></Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => setup.setAssistant('claude')}>{c.haveClaudeNow}</Button>
          </>) : (<>
            <Button size="sm" className="h-8 gap-1.5" onClick={joinWaitlist} disabled={listed || joining}>
              {listed ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Bell className="h-3.5 w-3.5" aria-hidden />}
              {listed ? c.onList : fill(c.tellMe, { assistant })}
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => setup.setAssistant('claude')}>{c.haveClaude}</Button>
            <CopyButton text={setupCopy.adminNotes.claude} label={c.itNote}
              onCopy={() => trackSetup('setup_admin_note_copied', { kind: 'claude' })} />
          </>)}
          <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={setup.hideCard}>{setupCopy.card.hide}</Button>
        </div>
      </div>
    </CardShell>
  );
}

// ── The rail tile ───────────────────────────────────────────────────────────

/** "Finish setup · n of 4" above the Setup item; Claude users with steps left only. */
export function FinishSetupTile({ collapsed }: { collapsed?: boolean }) {
  const setup = useSetup();
  const status = setup?.status;
  if (!setup || !status || status.aiClient !== 'claude' || status.complete || status.cardHidden) return null;
  const { doneCount, openStep } = setup;
  if (collapsed) return null;
  return (
    <button type="button" onClick={() => openStep('next')}
      className="mb-1 w-full rounded-xl border border-border bg-card/60 px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <span className="flex items-center justify-between text-sm">
        <span className="font-medium">{setupCopy.railTile.label}</span>
        <span className="text-[12.5px] text-muted-foreground">{fill(setupCopy.railTile.count, { n: doneCount })}</span>
      </span>
      <span className="mt-2 block h-1 overflow-hidden rounded-full bg-muted" aria-hidden>
        <span className="block h-full rounded-full bg-primary" style={{ width: `${(doneCount / 4) * 100}%` }} />
      </span>
    </button>
  );
}
